import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { renderMarkdown, hljs, renderMermaid } from "../markdown"
import { summarizeUserQuestionAnswer } from "../lib/user_question_summary.mjs"
import { collectUserQuestionPayload, collectUserQuestionSummaryFields } from "../lib/user_question_form.mjs"
import { selectedAgentUrl } from "../lib/agent_selection_url.mjs"
import { nextRouterMode } from "../lib/router_mode_selection.mjs"
import { agentMatchesQuery } from "../lib/agent_filter.mjs"

const DOC_EXTS = [".docx", ".doc", ".xlsx", ".xls", ".pptx", ".pdf", ".txt", ".md", ".markdown", ".html", ".htm", ".css", ".js", ".ts", ".json", ".xml", ".yaml", ".yml", ".csv", ".log", ".ini", ".conf", ".cfg", ".sh", ".bat", ".py", ".java", ".c", ".cpp", ".h", ".go", ".rs", ".rb", ".php", ".sql"]
const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".gif", ".webp"]
const ALL_SUPPORTED_EXTS = [...DOC_EXTS, ...IMAGE_EXTS]

function getFileExt(name) {
  return name.substring(name.lastIndexOf(".")).toLowerCase()
}

export default class extends Controller {
  static values = { conversationId: Number }
  static targets = ["messages", "form", "input", "sendBtn", "stopBtn", "uploadBtn", "fileInput", "filePreview", "agentSearch", "agentCard", "agentBar", "mobileTitle", "routerModes", "routerEntry", "agentEmpty"]

  connect() {
    this._activeBubble = null
    this._thinkingBlock = null
    this._subAgentBlock = null
    this._subAgentThinkingBlock = null
    this._subAgentOutputBlock = null
    this._subAgentRawContent = ""
    this._searchingBubble = null
    this._streaming = false
    this._transferring = false
    this._rawContent = ""
    this._composing = false
    this._pendingFiles = []
    this._selectedAgentId = this._getInitialAgentId()
    this._selectedRouterMode = nextRouterMode({ isRouter: this._isRouterCard(this._getActiveAgentCard()) })
    this._waitingForUserQuestion = false
    this._shareDialog = null
    this._handleDrawerKeydown = (event) => {
      if (event.key === "Escape") this.closeSidebars()
    }
    document.addEventListener("keydown", this._handleDrawerKeydown)

    if (this.hasConversationIdValue && this.conversationIdValue) {
      this.subscription = consumer.subscriptions.create(
        { channel: "ChatChannel", conversation_id: this.conversationIdValue },
        { received: this._handleReceived.bind(this) }
      )
    }
    this._renderExistingMessages()
    this._syncRouterModes()
    this._scrollToBottom()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.closeShareDialog()
    document.removeEventListener("keydown", this._handleDrawerKeydown)
    this.closeSidebars()
  }

  _getInitialAgentId() {
    const activeCard = this._getActiveAgentCard()
    if (activeCard && activeCard.dataset.agentId) {
      return activeCard.dataset.agentId || null
    }
    return null
  }

  _getActiveAgentCard() {
    return this.agentCardTargets.find(c => c.classList.contains("active"))
  }

  selectAgent(event) {
    const card = event.currentTarget
    const targetUrl = selectedAgentUrl(card.dataset.agentUrl || "")

    if (targetUrl !== `${window.location.pathname}${window.location.search}`) {
      window.location.href = targetUrl
      return
    }

    const agentId = card.dataset.agentId || null
    const name = card.dataset.agentName || "AI"
    const color = card.dataset.agentColor || ""
    const model = card.dataset.agentModel || "glm-5.1"
    const temp = card.dataset.agentTemperature || "0.7"

    this.agentCardTargets.forEach(c => c.classList.remove("active"))
    card.classList.add("active")

    this._selectedAgentId = agentId
    this._selectedRouterMode = nextRouterMode({ isRouter: this._isRouterCard(card) })
    this._showAgentBar(name, color, model, temp)
    this._syncRouterModes()
    this.inputTarget.placeholder = agentId ? `给 ${name} 发送消息...` : "Ask anything..."

    if (this.hasMobileTitleTarget) {
      this.mobileTitleTarget.textContent = agentId ? name : "AI Chat"
    }

    this.closeSidebars()
    this.inputTarget.focus()
  }

  _showAgentBar(name, color, model, temp) {
    if (!this.hasAgentBarTarget) return
    const bar = this.agentBarTarget
    bar.style.display = ""
    const colorVar = color || "var(--color-primary-500)"
    const title = bar.querySelector(".agent-bar-title")?.outerHTML || ""
    const actions = Array.from(bar.querySelectorAll(".agent-bar-edit-btn, .button_to"))
      .map(el => el.outerHTML)
      .join("")
    bar.innerHTML = `
      <span class="agent-bar-dot" style="background: ${colorVar}"></span>
      ${title}
      <span class="agent-bar-name">${this._escapeHTML(name)}</span>
      <span class="agent-bar-meta">${this._escapeHTML(model)} &middot; T: ${temp}</span>
      ${actions}
    `
  }

  _syncRouterModes() {
    if (!this.hasRouterModesTarget) return
    const activeCard = this._getActiveAgentCard()
    const isRouter = this._isRouterCard(activeCard)
    this.routerModesTarget.hidden = !isRouter
    if (this.hasRouterEntryTarget) this.routerEntryTarget.hidden = !isRouter
    this._syncRouterModeButtons()
  }

  _syncRouterModeButtons() {
    if (!this.hasRouterModesTarget) return
    this.routerModesTarget.querySelectorAll(".router-mode-btn").forEach(modeBtn => {
      modeBtn.classList.toggle("active", modeBtn.dataset.mode === this._selectedRouterMode)
    })
  }

  _isRouterCard(card) {
    return card?.dataset.agentRouter === "true"
  }

  triggerUpload() {
    this.fileInputTarget.click()
  }

  handleFileUpload() {
    const files = Array.from(this.fileInputTarget.files)
    if (!files.length) return

    const validFiles = files.filter(file => ALL_SUPPORTED_EXTS.includes(getFileExt(file.name)))

    if (!validFiles.length) {
      this._showError("没有支持的文件格式")
      this.fileInputTarget.value = ""
      return
    }

    const imageFiles = validFiles.filter(f => IMAGE_EXTS.includes(getFileExt(f.name)))
    const docFiles = validFiles.filter(f => !IMAGE_EXTS.includes(getFileExt(f.name)))

    this._setStreaming(true)
    const promises = []

    imageFiles.forEach(file => {
      promises.push(new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = (e) => {
          this._pendingFiles.push({ type: "image", filename: file.name, data: e.target.result })
          resolve()
        }
        reader.readAsDataURL(file)
      }))
    })

    if (docFiles.length) {
      const csrfToken = document.querySelector('[name="csrf-token"]').content
      const convertUrl = this.hasConversationIdValue && this.conversationIdValue
        ? `/conversations/${this.conversationIdValue}/convert_file`
        : "/convert_file"

      docFiles.forEach(file => {
        promises.push(
          (() => {
            const formData = new FormData()
            formData.append("file", file)
            if (this.hasConversationIdValue && this.conversationIdValue) {
              formData.append("conversation_id", this.conversationIdValue)
            }

            return fetch(convertUrl, {
              method: "POST",
              body: formData,
              headers: { "X-CSRF-Token": csrfToken }
            }).then(response => {
              if (!response.ok) return response.json().then(err => { throw new Error(err.error || "转换失败") })
              return response.json()
            }).then(data => {
              if (data.gbrain) {
                this._showGbrainResult(data.filename, data.result)
              } else {
                this._pendingFiles.push({ filename: data.filename, content: data.content })
              }
            })
          })()
        )
      })
    }

    Promise.all(promises).then(() => {
      if (this._pendingFiles.length) this._renderFilePreview()
    }).catch(err => {
      this._showError(err.message || "文件处理失败")
    }).finally(() => {
      this._setStreaming(false)
      this.fileInputTarget.value = ""
    })
  }

  submit(event) {
    event.preventDefault()

    const content = this.inputTarget.value.trim()
    if ((!content && !this._pendingFiles.length) || this._streaming) return

    const welcomeEl = this.messagesTarget.querySelector(".chat-welcome")
    if (welcomeEl) welcomeEl.remove()

    const textFiles = this._pendingFiles.filter(f => f.type !== "image")
    const images = this._pendingFiles.filter(f => f.type === "image")

    this._renderUserBubble(content, { files: textFiles, images })

    const messageContent = content
    const messageFiles = [...textFiles]
    const messageImages = [...images]

    this.inputTarget.value = ""
    this._autoResize()
    this._pendingFiles = []
    this._renderFilePreview()

    if (this.hasConversationIdValue && this.conversationIdValue) {
      this._sendMessage(messageContent, messageFiles, messageImages)
    } else {
      this._createAndSend(messageContent, messageFiles, messageImages)
    }
  }

  _sendMessage(content, textFiles, images) {
    const formData = new FormData()
    formData.append("message[content]", content)
    if (textFiles.length) formData.append("files", JSON.stringify(textFiles))
    if (images.length) formData.append("images", JSON.stringify(images))

    fetch(this.formTarget.action, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content }
    }).then(response => {
      if (!response.ok) throw new Error(response.status === 409 ? "当前对话正在生成中" : "发送失败，请重试")
    }).catch(() => {
      this._showError("发送失败，请重试")
      this._setStreaming(false)
    })
  }

  async _createAndSend(content, textFiles, images) {
    const csrfToken = document.querySelector('[name="csrf-token"]').content
    try {
      const createRes = await fetch("/conversations", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({
          agent_id: this._selectedAgentId || null,
          router_mode: this._selectedRouterMode || null
        })
      })
      if (!createRes.ok) throw new Error("创建对话失败")
      const { id, title } = await createRes.json()

      this.conversationIdValue = id
      this.formTarget.action = `/conversations/${id}/messages`

      await new Promise((resolve, reject) => {
        this.subscription = consumer.subscriptions.create(
          { channel: "ChatChannel", conversation_id: id },
          { received: this._handleReceived.bind(this), connected: resolve, rejected: reject }
        )
      })

      history.pushState({}, "", `/conversations/${id}`)
      this._addToHistorySidebar(id, title)

      const formData = new FormData()
      formData.append("message[content]", content)
      if (textFiles.length) formData.append("files", JSON.stringify(textFiles))
      if (images.length) formData.append("images", JSON.stringify(images))

      fetch(`/conversations/${id}/messages`, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": csrfToken }
      }).then(response => {
        if (!response.ok) throw new Error(response.status === 409 ? "当前对话正在生成中" : "发送失败，请重试")
      }).catch(() => {
        this._showError("发送失败，请重试")
        this._setStreaming(false)
      })
    } catch (err) {
      this._showError(err.message || "创建对话失败，请重试")
      this._setStreaming(false)
    }
  }

  _addToHistorySidebar(id, title) {
    const nav = this.element.querySelector(".conversation-list")
    if (!nav) return

    const noConv = nav.querySelector(".conversation-list-empty")
    if (noConv) noConv.remove()

    const link = document.createElement("a")
    link.href = `/conversations/${id}`
    link.className = "conversation-link conversation-list-card active"

    const activeAgentCard = this.agentCardTargets.find(card => card.classList.contains("active"))
    const agentName = activeAgentCard?.dataset.agentName || "General"

    link.innerHTML = `
      <div class="conversation-list-card-title-row">
        <span class="conversation-list-card-title">${this._escapeHTML(title)}</span>
        <time class="conversation-list-card-time">Just now</time>
      </div>
      <div class="conversation-list-card-meta">
        <span>${this._escapeHTML(agentName)}</span>
        <span>New conversation</span>
      </div>
    `

    const currentActive = nav.querySelector(".conversation-link.active")
    if (currentActive) currentActive.classList.remove("active")

    const firstCard = nav.querySelector(".conversation-list-card")
    if (firstCard) {
      nav.insertBefore(link, firstCard)
    } else {
      nav.appendChild(link)
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey && !this._composing) {
      event.preventDefault()
      this.submit(event)
    }
  }

  composingStart() {
    this._composing = true
  }

  composingEnd() {
    this._composing = false
  }

  stopStreaming() {
    if (!this.hasConversationIdValue || !this.conversationIdValue) return
    fetch(`/conversations/${this.conversationIdValue}/stop`, {
      method: "POST",
      headers: { "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content }
    })
    if (this.hasStopBtnTarget) this.stopBtnTarget.disabled = true
  }

  async createShare(event) {
    event.preventDefault()

    const form = event.currentTarget
    const submit = event.submitter || form.querySelector("[type='submit']")
    if (submit) submit.disabled = true

    try {
      const res = await fetch(form.action, {
        method: form.method || "POST",
        body: new FormData(form),
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content
        }
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "创建分享失败")

      const copied = await this._copyText(data.url)
      this._showShareDialog(data.url, copied)
    } catch (err) {
      this._showShareDialog("", false, err.message || "创建分享失败")
    } finally {
      if (submit) submit.disabled = false
    }
  }

  autoResize() {
    this._autoResize()
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (!isNaN(index) && index >= 0 && index < this._pendingFiles.length) {
      this._pendingFiles.splice(index, 1)
      this._renderFilePreview()
    }
  }

  submitUserQuestion(event) {
    event.preventDefault()
    const form = event.currentTarget
    this._sendUserQuestionAnswer(form, false)
  }

  declineUserQuestion(event) {
    const form = event.currentTarget.closest("form")
    if (!form) return
    this._sendUserQuestionAnswer(form, true)
  }

  // --- Sidebar toggling ---

  toggleAgentSidebar() {
    if (this.element.classList.contains("conversation-workspace")) {
      document.body.classList.toggle("nav-open")
      this._syncDrawerState()
      return
    }

    const sidebar = document.getElementById("agent-sidebar")
    const overlay = document.getElementById("sidebar-overlay")
    const history = document.getElementById("history-sidebar")
    if (history) history.classList.remove("open")
    sidebar.classList.toggle("open")
    if (overlay) overlay.classList.toggle("visible", sidebar.classList.contains("open"))
  }

  toggleHistorySidebar() {
    const sidebar = document.getElementById("history-sidebar")
    const overlay = document.getElementById("sidebar-overlay")
    const agent = document.getElementById("agent-sidebar")
    if (!sidebar) return
    if (agent) agent.classList.remove("open")
    sidebar.classList.toggle("open")
    if (overlay) overlay.classList.toggle("visible", sidebar.classList.contains("open"))
  }

  showAgentSidebar() {
    const sidebar = document.getElementById("agent-sidebar")
    const overlay = document.getElementById("sidebar-overlay")
    const history = document.getElementById("history-sidebar")
    if (history) history.classList.remove("open")
    if (sidebar) sidebar.classList.add("open")
    if (overlay) overlay.classList.add("visible")
  }

  closeSidebars() {
    document.body.classList.remove("nav-open")

    const agent = document.getElementById("agent-sidebar")
    const history = document.getElementById("history-sidebar")
    const overlay = document.getElementById("sidebar-overlay")
    if (agent) agent.classList.remove("open")
    if (history) history.classList.remove("open")
    if (overlay) overlay.classList.remove("visible")
    this._syncDrawerState()
  }

  _syncDrawerState() {
    const isOpen = document.body.classList.contains("nav-open")
    this.element.querySelectorAll(".mobile-menu-btn").forEach(button => {
      button.setAttribute("aria-expanded", String(isOpen))
      button.setAttribute("aria-label", isOpen ? "关闭侧栏" : "打开侧栏")
    })
  }

  focusComposer() {
    if (!this.hasInputTarget) return
    this.inputTarget.focus()
  }

  // --- Agent filtering ---

  filterAgents() {
    const query = this.agentSearchTarget.value.toLowerCase()
    let visibleCount = 0

    this._agentListCards().forEach(card => {
      const visible = agentMatchesQuery({
        name: card.dataset.agentName || "",
        description: card.dataset.agentDescription || ""
      }, query)
      card.hidden = !visible
      if (visible) visibleCount += 1
    })

    if (this.hasAgentEmptyTarget) {
      this.agentEmptyTarget.hidden = visibleCount > 0
    }
  }

  _agentListCards() {
    if (!this.hasAgentListTarget) return this.agentCardTargets
    return this.agentCardTargets.filter(card => this.agentListTarget.contains(card))
  }

  // --- Quick prompt from welcome page ---

  sendPrompt(event) {
    const prompt = event.currentTarget.dataset.prompt
    if (!prompt || !this.hasInputTarget) return
    this.inputTarget.value = prompt
    this._autoResize()
    this.formTarget.requestSubmit()
  }

  startRouterMode(event) {
    const btn = event.currentTarget
    const agentId = btn.dataset.routerAgentId
    const mode = btn.dataset.mode
    if (!agentId || !mode) return

    const card = this.agentCardTargets.find(c => c.dataset.agentId === agentId)
    if (card) {
      const targetUrl = selectedAgentUrl(card.dataset.agentUrl || "")
      if (targetUrl !== `${window.location.pathname}${window.location.search}`) {
        window.location.href = targetUrl
        return
      }
    }

    if (card) {
      this.agentCardTargets.forEach(c => c.classList.remove("active"))
      card.classList.add("active")
      this._selectedAgentId = agentId
      const name = card.dataset.agentName || "Router"
      const color = card.dataset.agentColor || ""
      const model = card.dataset.agentModel || "glm-5.1"
      const temp = card.dataset.agentTemperature || "0.7"
      this._showAgentBar(name, color, model, temp)
      this._syncRouterModes()
      this.inputTarget.placeholder = `给 ${name} 发送消息...`
      if (this.hasMobileTitleTarget) this.mobileTitleTarget.textContent = name
    }

    this._selectedRouterMode = nextRouterMode({ isRouter: true, requestedMode: mode })
    this._syncRouterModeButtons()
    this.inputTarget.focus()
  }

  // --- private ---

  _handleReceived(data) {
    switch (data.event) {
      case "stream_status":
        this._handleStreamStatus(data)
        break
      case "stream_snapshot":
        this._restoreStreamSnapshot(data)
        break
      case "stream_start":
        this._createAssistantBubble()
        break
      case "chunk":
        this._appendChunk(data.content)
        break
      case "thinking":
        this._appendThinking(data.content)
        break
      case "sub_agent_thinking":
        this._appendSubAgentThinking(data)
        break
      case "sub_agent_chunk":
        this._appendSubAgentChunk(data)
        break
      case "tool_use":
        this._showToolUse(data.tool_name, data.tool_args)
        break
      case "ask_user_question":
        this._showUserQuestion(data)
        break
      case "page_created":
        this._showPageCreated(data)
        break
      case "page_updated":
        this._showPageUpdated(data)
        break
      case "searching":
        this._showSearching(data.query)
        break
      case "search_done":
        this._hideSearching()
        break
      case "file_download":
        this._appendFileDownload(data.url, data.format)
        break
      case "stream_end":
        this._finalizeBubble()
        if (this._transferring) {
          this._transferring = false
        }
        break
      case "agent_transfer":
        this._handleAgentTransfer(data)
        break
      case "error":
        this._showError(data.content)
        this._setStreaming(false)
        break
    }
  }

  _createAvatar(role) {
    const avatar = document.createElement("div")
    avatar.className = "message-avatar"
    if (role === "assistant") {
      avatar.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2a4 4 0 0 1 4 4v1a1 1 0 0 0 1 1h1a4 4 0 0 1 0 8h-1a1 1 0 0 0-1 1v1a4 4 0 0 1-8 0v-1a1 1 0 0 0-1-1H6a4 4 0 0 1 0-8h1a1 1 0 0 0 1-1V6a4 4 0 0 1 4-4z"/></svg>'
    } else {
      avatar.textContent = "U"
    }
    return avatar
  }

  _renderUserBubble(text, options = {}) {
    const wrapper = document.createElement("div")
    wrapper.className = "message user"
    wrapper.appendChild(this._createAvatar("user"))

    const bubble = document.createElement("div")
    bubble.className = "bubble"
    if ((options.images && options.images.length) || (options.files && options.files.length)) {
      bubble.classList.add("has-attachments")
    }

    if (text) {
      const textEl = document.createElement("div")
      textEl.className = "bubble-text"
      textEl.textContent = text
      bubble.appendChild(textEl)
    }

    if (options.images && options.images.length) {
      const imgContainer = document.createElement("div")
      imgContainer.className = "bubble-images"
      options.images.forEach(img => {
        const a = document.createElement("a")
        a.href = img.data
        a.target = "_blank"
        const imgEl = document.createElement("img")
        imgEl.src = img.data
        imgEl.className = "bubble-image"
        imgEl.loading = "lazy"
        a.appendChild(imgEl)
        imgContainer.appendChild(a)
      })
      bubble.appendChild(imgContainer)
    }

    if (options.files && options.files.length) {
      const fileContainer = document.createElement("div")
      fileContainer.className = "bubble-files"
      options.files.forEach(f => {
        if (f.path) {
          const link = document.createElement("a")
          link.href = f.path
          link.className = "bubble-file-link"
          link.target = "_blank"
          link.download = f.filename
          link.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg> ${this._escapeHTML(f.filename)}`
          fileContainer.appendChild(link)
        }
      })
      bubble.appendChild(fileContainer)
    }

    wrapper.appendChild(bubble)
    this.messagesTarget.appendChild(wrapper)
    this._scrollToBottom()
  }

  _renderFilePreview() {
    if (!this.hasFilePreviewTarget) return
    const container = this.filePreviewTarget
    container.innerHTML = ""

    this._pendingFiles.forEach((file, index) => {
      const tag = document.createElement("div")
      tag.className = "file-preview-tag"

      if (file.type === "image" && file.data) {
        const img = document.createElement("img")
        img.src = file.data
        img.className = "image-preview-thumb"
        tag.appendChild(img)
      } else {
        const icon = document.createElement("span")
        icon.className = "file-preview-icon"
        icon.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>'
        tag.appendChild(icon)
      }

      const name = document.createElement("span")
      name.className = "file-preview-name"
      name.textContent = file.filename
      tag.appendChild(name)

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "file-preview-remove"
      removeBtn.dataset.index = index
      removeBtn.dataset.action = "click->chat#removeFile"
      removeBtn.innerHTML = "&times;"
      removeBtn.title = "移除"
      tag.appendChild(removeBtn)

      container.appendChild(tag)
    })
  }

  _showGbrainResult(filename, result) {
    const emptyState = this.messagesTarget.querySelector(".chat-empty")
    if (emptyState) emptyState.remove()

    const wrapper = document.createElement("div")
    wrapper.className = "message assistant"
    wrapper.appendChild(this._createAvatar("assistant"))

    const bubble = document.createElement("div")
    bubble.className = "bubble"

    const isError = typeof result === "string" && result.startsWith("Error:")
    if (isError) {
      bubble.classList.add("error")
      bubble.textContent = `写入 gbrain 失败 (${filename}): ${result}`
    } else {
      bubble.textContent = `已写入 gbrain: ${filename}`
    }

    wrapper.appendChild(bubble)
    this.messagesTarget.appendChild(wrapper)
    this._scrollToBottom()
  }

  _createAssistantBubble() {
    const wrapper = document.createElement("div")
    wrapper.className = "message assistant"
    wrapper.appendChild(this._createAvatar("assistant"))

    const bubble = document.createElement("div")
    bubble.className = "bubble"
    bubble.innerHTML = '<span class="typing-indicator"><span></span><span></span><span></span></span>'
    bubble.dataset.rawContent = ""
    wrapper.appendChild(bubble)

    this.messagesTarget.appendChild(wrapper)
    this._activeBubble = bubble
    this._rawContent = ""
    this._scrollToBottom()
  }

  _restoreStreamSnapshot(data) {
    if (!data.active || this._activeBubble) return

    const welcomeEl = this.messagesTarget.querySelector(".chat-welcome")
    if (welcomeEl) welcomeEl.remove()

    this._createAssistantBubble()

    const thinking = data.thinking_content || ""
    if (thinking) this._appendThinking(thinking)

    const content = data.content || ""
    if (content) {
      this._appendChunk(content)
    }
  }

  _handleStreamStatus(data) {
    this._setStreaming(!!data.active)
  }

  _appendChunk(text) {
    if (!this._activeBubble) return

    const indicator = this._activeBubble.querySelector(".typing-indicator")
    if (indicator) indicator.remove()

    this._rawContent += text
    this._activeBubble.dataset.rawContent = this._rawContent

    const preserved = this._activeBubble.querySelectorAll(".thinking-block, .sub-agent-block, .tool-use-tag, .file-download-tag, .ask-user-question-form, .ask-user-question-summary")
    this._activeBubble.innerHTML = renderMarkdown(this._rawContent)
    Array.from(preserved).reverse().forEach(el => this._activeBubble.prepend(el))

    this._scrollToBottom()
  }

  _appendThinking(text) {
    if (!this._activeBubble) return

    const indicator = this._activeBubble.querySelector(".typing-indicator")
    if (indicator) indicator.remove()

    if (!this._thinkingBlock) {
      const details = document.createElement("details")
      details.className = "thinking-block"
      const summary = document.createElement("summary")
      summary.textContent = "思考过程"
      details.appendChild(summary)
      this._thinkingBlock = document.createElement("div")
      this._thinkingBlock.className = "thinking-content"
      details.appendChild(this._thinkingBlock)
      this._activeBubble.appendChild(details)
    }

    this._thinkingBlock.textContent += text
    this._scrollToBottom()
  }

  _ensureSubAgentBlock(data = {}) {
    if (!this._activeBubble) return null

    const indicator = this._activeBubble.querySelector(".typing-indicator")
    if (indicator) indicator.remove()

    if (!this._subAgentBlock) {
      const details = document.createElement("details")
      details.className = "sub-agent-block"
      details.open = true

      const summary = document.createElement("summary")
      const color = data.agent_color || "var(--color-primary-500)"
      summary.innerHTML = `
        <span class="sub-agent-dot" style="background: ${color}"></span>
        <span>子 Agent: ${this._escapeHTML(data.agent_name || "Agent")}</span>
      `
      details.appendChild(summary)

      this._subAgentThinkingBlock = document.createElement("div")
      this._subAgentThinkingBlock.className = "sub-agent-thinking"
      this._subAgentThinkingBlock.hidden = true
      details.appendChild(this._subAgentThinkingBlock)

      this._subAgentOutputBlock = document.createElement("div")
      this._subAgentOutputBlock.className = "sub-agent-output"
      details.appendChild(this._subAgentOutputBlock)

      this._activeBubble.appendChild(details)
      this._subAgentBlock = details
    }

    return this._subAgentBlock
  }

  _appendSubAgentThinking(data) {
    if (!this._ensureSubAgentBlock(data)) return

    if (this._subAgentThinkingBlock.hidden) {
      const label = document.createElement("div")
      label.className = "sub-agent-section-label"
      label.textContent = "思考过程"
      this._subAgentThinkingBlock.appendChild(label)

      const content = document.createElement("div")
      content.className = "sub-agent-thinking-content"
      this._subAgentThinkingBlock.appendChild(content)
      this._subAgentThinkingBlock.hidden = false
    }

    const content = this._subAgentThinkingBlock.querySelector(".sub-agent-thinking-content")
    content.textContent += data.content || ""
    this._scrollToBottom()
  }

  _appendSubAgentChunk(data) {
    if (!this._ensureSubAgentBlock(data)) return

    this._subAgentRawContent += data.content || ""
    this._subAgentOutputBlock.innerHTML = renderMarkdown(this._subAgentRawContent)
    this._scrollToBottom()
  }

  _appendFileDownload(url, format) {
    if (!this._activeBubble) return
    const indicator = this._activeBubble.querySelector(".typing-indicator")
    if (indicator) indicator.remove()
    const ext = format === "pdf" ? "PDF" : (format === "image" ? "图片" : "Word")
    const link = document.createElement("a")
    link.href = url
    link.className = "file-download-tag"
    link.target = "_blank"
    link.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> 下载 ${ext}`
    this._activeBubble.appendChild(link)
    this._scrollToBottom()
  }

  _showToolUse(toolName, toolArgs) {
    if (!this._activeBubble) return
    const indicator = this._activeBubble.querySelector(".typing-indicator")
    if (indicator) indicator.remove()

    const tag = document.createElement("div")
    const isKbTool = toolName === "create_knowledge_page" || toolName === "update_knowledge_page"
    const isExportTool = toolName === "export_pdf" || toolName === "export_word"

    if (isKbTool) {
      tag.className = "tool-use-tag kb-tool-use-tag"
      const bookIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>'
      let label = toolName === "create_knowledge_page" ? "创建知识页面" : "更新知识页面"
      if (toolArgs) {
        try {
          const args = JSON.parse(toolArgs)
          if (args.title) label += `: ${args.title}`
        } catch (_e) { /* ignore parse error */ }
      }
      tag.innerHTML = bookIcon + `<span>${this._escapeHTML(label)}</span>`
    } else if (isExportTool) {
      tag.className = "tool-use-tag export-tool-use-tag"
      const exportIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'
      const label = toolName === "export_pdf" ? "导出 PDF" : "导出 Word"
      tag.innerHTML = exportIcon + `<span>${label}</span>`
    } else {
      tag.className = "tool-use-tag"
      const searchIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>'
      let label = toolName || "tool"
      if (toolArgs) {
        try {
          const args = JSON.parse(toolArgs)
          if (args.search_query) label = args.search_query
        } catch (_e) { /* ignore parse error */ }
      }
      tag.innerHTML = searchIcon + `<span>${this._escapeHTML(label)}</span>`
    }

    this._activeBubble.appendChild(tag)
    this._scrollToBottom()
  }

  _showUserQuestion(data) {
    if (!this._activeBubble) this._createAssistantBubble()

    const indicator = this._activeBubble.querySelector(".typing-indicator")
    if (indicator) indicator.remove()

    const form = document.createElement("form")
    form.className = "ask-user-question-form"
    form.dataset.pendingQuestionId = data.pending_question_id
    form.dataset.action = "submit->chat#submitUserQuestion"

    const title = document.createElement("div")
    title.className = "ask-user-question-title"
    title.textContent = data.question || "需要你补充信息"
    form.appendChild(title)

    const fields = Array.isArray(data.fields) ? data.fields : []
    fields.forEach(field => {
      form.appendChild(this._buildUserQuestionField(field))
    })

    const error = document.createElement("div")
    error.className = "ask-user-question-error"
    error.hidden = true
    form.appendChild(error)

    const actions = document.createElement("div")
    actions.className = "ask-user-question-actions"

    const submit = document.createElement("button")
    submit.type = "submit"
    submit.className = "ask-user-question-submit"
    submit.textContent = "提交回答"
    actions.appendChild(submit)

    const decline = document.createElement("button")
    decline.type = "button"
    decline.className = "ask-user-question-decline"
    decline.dataset.action = "click->chat#declineUserQuestion"
    decline.textContent = "拒绝回答"
    actions.appendChild(decline)

    form.appendChild(actions)
    this._activeBubble.appendChild(form)
    this._waitingForUserQuestion = true
    this._scrollToBottom()
  }

  _buildUserQuestionField(field) {
    const wrapper = document.createElement("div")
    wrapper.className = "ask-user-question-field"
    wrapper.dataset.fieldType = field.type
    wrapper.dataset.fieldId = field.id

    const label = document.createElement("div")
    label.className = "ask-user-question-label"
    label.textContent = field.label || field.id
    wrapper.appendChild(label)

    if (field.type === "text") {
      const input = document.createElement("input")
      input.type = "text"
      input.name = field.id
      input.required = true
      input.placeholder = field.placeholder || ""
      input.className = "ask-user-question-input"
      wrapper.appendChild(input)
      return wrapper
    }

    const options = document.createElement("div")
    options.className = "ask-user-question-options"
    const optionValues = Array.isArray(field.options) ? field.options : []

    optionValues.forEach((option, index) => {
      const optionId = `${field.id}_${index}`
      const optionLabel = document.createElement("label")
      optionLabel.className = "ask-user-question-option"

      const input = document.createElement("input")
      input.type = field.type === "checkbox" ? "checkbox" : "radio"
      input.name = field.id
      input.value = option
      input.required = field.type === "radio"
      input.id = optionId

      const span = document.createElement("span")
      span.textContent = option

      optionLabel.appendChild(input)
      optionLabel.appendChild(span)
      options.appendChild(optionLabel)
    })

    wrapper.appendChild(options)
    return wrapper
  }

  _sendUserQuestionAnswer(form, declined) {
    if (!this.hasConversationIdValue || !this.conversationIdValue) return

    const pendingId = form.dataset.pendingQuestionId
    const error = form.querySelector(".ask-user-question-error")
    if (!declined && !this._validateUserQuestionForm(form, error)) return

    const answerFields = collectUserQuestionSummaryFields(form)
    form.querySelectorAll("button, input").forEach(el => { el.disabled = true })
    if (error) error.hidden = true

    const formData = new FormData()
    formData.append("pending_question_id", pendingId)
    formData.append("declined", declined ? "true" : "false")

    if (!declined) {
      collectUserQuestionPayload(form).forEach(([name, value]) => {
        formData.append(name, value)
      })
    }

    this._waitingForUserQuestion = false

    fetch(`/conversations/${this.conversationIdValue}/answer_question`, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content }
    }).then(response => {
      if (!response.ok) throw new Error("提交失败，请检查必填项")
      return true
    }).catch(err => {
      return this._handleUserQuestionSubmitError(form, error, err)
    }).then(ok => {
      if (!ok) return
      form.replaceWith(this._buildUserQuestionSummary(answerFields, declined))
    })
  }

  _handleUserQuestionSubmitError(form, error, err) {
    this._waitingForUserQuestion = true
    form.querySelectorAll("button, input").forEach(el => { el.disabled = false })
    if (error) {
      error.textContent = err.message || "提交失败"
      error.hidden = false
    }
    return false
  }

  _buildUserQuestionSummary(fields, declined) {
    const summaryData = summarizeUserQuestionAnswer(fields, declined)
    const summary = document.createElement("div")
    summary.className = `ask-user-question-summary ${declined ? "declined" : "answered"}`

    const status = document.createElement("div")
    status.className = "ask-user-question-summary-status"
    status.textContent = summaryData.status
    summary.appendChild(status)

    if (summaryData.items.length) {
      const list = document.createElement("dl")
      list.className = "ask-user-question-summary-list"
      summaryData.items.forEach(item => {
        const term = document.createElement("dt")
        term.textContent = item.label
        const detail = document.createElement("dd")
        detail.textContent = item.value
        list.appendChild(term)
        list.appendChild(detail)
      })
      summary.appendChild(list)
    }

    return summary
  }

  _validateUserQuestionForm(form, error) {
    let valid = true
    form.querySelectorAll(".ask-user-question-field").forEach(field => {
      field.classList.remove("invalid")
      const type = field.dataset.fieldType
      let fieldValid = true
      if (type === "checkbox") {
        fieldValid = !!field.querySelector("input:checked")
      } else {
        const input = field.querySelector("input")
        fieldValid = !!input && !!input.value.trim()
      }
      if (!fieldValid) {
        valid = false
        field.classList.add("invalid")
      }
    })

    if (!valid && error) {
      error.textContent = "请填写所有必填项，或拒绝回答。"
      error.hidden = false
    }
    return valid
  }

  _finalizeBubble() {
    if (this._activeBubble) {
      if (this._rawContent) {
        const preserved = this._activeBubble.querySelectorAll(".thinking-block, .sub-agent-block, .tool-use-tag, .file-download-tag, .ask-user-question-form, .ask-user-question-summary")
        this._activeBubble.dataset.rawContent = this._rawContent
        this._activeBubble.innerHTML = renderMarkdown(this._rawContent)
        Array.from(preserved).reverse().forEach(el => this._activeBubble.prepend(el))
        this._activeBubble.appendChild(this._buildMessageFooter())
      }
      this._highlightCode(this._activeBubble)
      renderMermaid(this._activeBubble)
    }
    this._activeBubble = null
    this._thinkingBlock = null
    this._subAgentBlock = null
    this._subAgentThinkingBlock = null
    this._subAgentOutputBlock = null
    this._subAgentRawContent = ""
    this._rawContent = ""
  }

  _buildMessageFooter() {
    const footer = document.createElement("div")
    footer.className = "message-footer"

    const button = document.createElement("button")
    button.type = "button"
    button.className = "message-copy-btn"
    button.dataset.action = "click->chat#copyRawMessage"
    button.title = "复制原文"
    button.setAttribute("aria-label", "复制原文")
    button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
    footer.appendChild(button)

    return footer
  }

  _showSearching(query) {
    const wrapper = document.createElement("div")
    wrapper.className = "message assistant"
    wrapper.appendChild(this._createAvatar("assistant"))

    const bubble = document.createElement("div")
    bubble.className = "bubble searching"
    bubble.innerHTML = `<span class="search-spinner"></span> 正在搜索: ${this._escapeHTML(query)}`
    wrapper.appendChild(bubble)

    this.messagesTarget.appendChild(wrapper)
    this._searchingBubble = wrapper
    this._scrollToBottom()
  }

  _hideSearching() {
    if (this._searchingBubble) {
      this._searchingBubble.remove()
      this._searchingBubble = null
    }
  }

  _handleAgentTransfer(data) {
    this._finalizeBubble()
    this._transferring = true

    const transferBubble = document.createElement("div")
    transferBubble.className = "message assistant"
    transferBubble.appendChild(this._createAvatar("assistant"))

    const bubble = document.createElement("div")
    bubble.className = "bubble agent-transfer-notice"
    bubble.innerHTML = `<span class="transfer-icon">&#x1F517;</span> 已转交至 <strong>${this._escapeHTML(data.agent_name)}</strong> 处理`
    transferBubble.appendChild(bubble)

    this.messagesTarget.appendChild(transferBubble)

    if (this.hasAgentBarTarget) {
      const colorVar = data.agent_color || "var(--color-primary-500)"
      this.agentBarTarget.innerHTML = `
        <span class="agent-bar-dot" style="background: ${colorVar}"></span>
        <span class="agent-bar-name">${this._escapeHTML(data.agent_name)}</span>
        <span class="agent-bar-meta">${this._escapeHTML(data.agent_model)} &middot; T: ${data.agent_temperature}</span>
      `
      this.agentBarTarget.style.display = ""
    }

    if (this.hasMobileTitleTarget) {
      this.mobileTitleTarget.textContent = data.agent_name
    }

    this._scrollToBottom()
  }

  _showError(text) {
    const wrapper = document.createElement("div")
    wrapper.className = "message assistant"
    wrapper.appendChild(this._createAvatar("assistant"))

    const bubble = document.createElement("div")
    bubble.className = "bubble error"
    bubble.textContent = text
    wrapper.appendChild(bubble)

    this.messagesTarget.appendChild(wrapper)
    this._activeBubble = null
    this._scrollToBottom()
  }

  _setStreaming(value) {
    this._streaming = value
    this.inputTarget.disabled = value
    if (this.hasSendBtnTarget) this.sendBtnTarget.style.display = value ? "none" : ""
    if (this.hasStopBtnTarget) {
      this.stopBtnTarget.style.display = value ? "" : "none"
      this.stopBtnTarget.disabled = false
    }
    if (!value) this.inputTarget.focus()
  }

  _scrollToBottom() {
    const el = this.messagesTarget
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight })
  }

  _autoResize() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 150) + "px"
  }

  _highlightCode(container) {
    container.querySelectorAll("pre code").forEach(block => {
      hljs.highlightElement(block)
    })
  }

  copyRawMessage(event) {
    const button = event.currentTarget
    const bubble = button.closest(".bubble")
    const raw = bubble?.dataset.rawContent || ""
    if (!raw) return

    navigator.clipboard.writeText(raw).then(() => {
      const originalLabel = button.getAttribute("aria-label") || "复制原文"
      button.classList.add("copied")
      button.setAttribute("aria-label", "已复制")
      button.title = "已复制"
      window.setTimeout(() => {
        button.classList.remove("copied")
        button.setAttribute("aria-label", originalLabel)
        button.title = originalLabel
      }, 1200)
    }).catch(() => {
      button.title = "复制失败"
    })
  }

  async _copyText(text) {
    if (!text || !navigator.clipboard) return false

    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      return false
    }
  }

  _showShareDialog(url, copied, error = "") {
    this._shareDialog?.remove()

    const dialog = document.createElement("dialog")
    dialog.className = "share-dialog"
    dialog.innerHTML = `
      <div class="share-dialog-card">
        <div class="share-dialog-header">
          <div>
            <h2>${error ? "分享失败" : "分享链接已生成"}</h2>
            <p>${error ? this._escapeHTML(error) : (copied ? "链接已自动复制到剪切板。" : "请手动复制以下链接。")}</p>
          </div>
          <button type="button" class="share-dialog-close" data-share-dialog-close data-action="click->chat#closeShareDialog" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
          </button>
        </div>
        ${url ? `
          <div class="share-dialog-url-row">
            <input class="share-dialog-url" value="${this._escapeHTML(url)}" readonly>
            <button type="button" class="btn btn-secondary btn-sm" data-share-dialog-copy data-action="click->chat#copyShareDialogUrl">Copy</button>
          </div>
          <a class="btn btn-primary btn-sm" href="${this._escapeHTML(url)}" target="_blank" rel="noopener">Open link</a>
        ` : ""}
      </div>
    `

    dialog.addEventListener("click", (event) => {
      if (event.target.closest("[data-share-dialog-copy]")) {
        this.copyShareDialogUrl()
        return
      }
      if (event.target.closest("[data-share-dialog-close]")) {
        this.closeShareDialog()
      }
      if (event.target === dialog) {
        this.closeShareDialog()
      }
    })

    document.body.appendChild(dialog)
    this._shareDialog = dialog
    dialog.showModal()
    dialog.querySelector(".share-dialog-url")?.select()
  }

  closeShareDialog() {
    if (!this._shareDialog) return
    this._shareDialog.close()
    this._shareDialog.remove()
    this._shareDialog = null
  }

  copyShareDialogUrl() {
    const input = this._shareDialog?.querySelector(".share-dialog-url")
    if (!input) return
    this._copyText(input.value)
    input.select()
    this.closeShareDialog()
  }

  _renderExistingMessages() {
    this.messagesTarget.querySelectorAll("[data-markdown-content]").forEach(bubble => {
      const raw = bubble.dataset.markdownContent
      bubble.removeAttribute("data-markdown-content")
      const preservedTop = bubble.querySelectorAll(".thinking-block, .sub-agent-block, .tool-use-tag, .file-download-tag, .ask-user-question-form, .ask-user-question-summary")
      const footer = bubble.querySelector(".message-footer")
      bubble.innerHTML = renderMarkdown(raw)
      Array.from(preservedTop).reverse().forEach(el => bubble.prepend(el))
      if (footer) bubble.appendChild(footer)
      this._highlightCode(bubble)
      renderMermaid(bubble)
    })
  }

  _escapeHTML(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
